import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface Props {
  user: any;
}

export default function ProfileInfo({ user }: Props) {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id]); // User ID değiştiğinde veriyi çek

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle(); // .single() yerine .maybeSingle() daha güvenlidir

      if (error) throw error;

      if (data) {
        setFullName(data.full_name || "");
        setPhone(data.phone || "");
        setNotificationEnabled(data.notification_enabled ?? true);
        setEmailNotifications(data.email_notifications ?? true);
      }
    } catch (err) {
      console.error("Profil yüklenirken hata:", err);
    }
  };

  // 1. KRİTİK DÜZELTME: Kullanıcı verisi gelene kadar bir yükleme ekranı göster
  if (!user) {
    return (
      <div className="max-w-2xl bg-white rounded-2xl p-8 border border-gray-100 text-center text-gray-400 italic">
        Kullanıcı oturumu doğrulanıyor...
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      const { error: upsertError } = await supabase
        .from("user_profiles")
        .upsert({
          id: user.id,
          full_name: fullName,
          phone: phone,
          notification_enabled: notificationEnabled,
          email_notifications: emailNotifications,
          updated_at: new Date().toISOString(),
        });

      if (upsertError) throw upsertError;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || "Profil güncellenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Avatar Kısmı - user?.email kullanımıyla korundu */}
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold text-white">
                {fullName
                  ? fullName.charAt(0).toUpperCase()
                  : user?.email?.charAt(0).toUpperCase() || "?"}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {fullName || "İsimsiz Kullanıcı"}
              </h3>
              <p className="text-gray-600">{user?.email}</p>
            </div>
          </div>

          {/* Ad Soyad */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Ad Soyad
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Adınız Soyadınız"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
            />
          </div>

          {/* E-posta (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              E-posta Adresi
            </label>
            <input
              type="email"
              value={user?.email || ""}
              disabled
              className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
            />
          </div>

          {/* Telefon Numarası */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Telefon Numarası
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+90 5XX XXX XX XX"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
            />
          </div>

          {/* Bildirim Tercihleri (Toggle Butonlar) */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Bildirim Tercihleri
            </h3>

            {/* Bildirim Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Push Bildirimleri</p>
                <p className="text-sm text-gray-600">
                  Fiyat düşüşleri ve güncellemeler
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNotificationEnabled(!notificationEnabled)}
                className={`relative w-14 h-8 rounded-full transition-colors ${notificationEnabled ? "bg-rose-500" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${notificationEnabled ? "translate-x-6" : "translate-x-0"}`}
                />
              </button>
            </div>

            {/* E-posta Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">
                  E-posta Bildirimleri
                </p>
                <p className="text-sm text-gray-600">
                  Önemli güncellemeleri mail ile al
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEmailNotifications(!emailNotifications)}
                className={`relative w-14 h-8 rounded-full transition-colors ${emailNotifications ? "bg-rose-500" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${emailNotifications ? "translate-x-6" : "translate-x-0"}`}
                />
              </button>
            </div>
          </div>

          {/* Mesajlar ve Buton */}
          {success && (
            <div className="p-4 bg-emerald-50 text-emerald-600 rounded-xl text-sm">
              Profil başarıyla güncellendi!
            </div>
          )}
          {error && (
            <div className="p-4 bg-rose-50 text-rose-600 rounded-xl text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-rose-500 text-white rounded-xl font-semibold hover:bg-rose-600 transition-colors disabled:opacity-50"
          >
            {loading ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </form>
      </div>
    </div>
  );
}
